import {
  Check,
  ChevronDown,
  ChevronRight,
  Pencil,
  ScanLine,
  Search,
  UserPlus,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";

import {
  FIELD,
  FIELD_LABEL,
  NumberStepper,
  PRIMARY_BUTTON,
  RegistrationEditor,
  SECONDARY_BUTTON,
} from "../../components/GuestForm";
import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";
import { isNotComing } from "../event-registrations/notComing";
import {
  type EventGroup,
  type Registration,
} from "../event-registrations/shared";

import { clockTime, peopleSummary } from "./GuestCard";
import { KundGround } from "./KundGround";

const SEARCH_LABEL = "Search by name, phone or reference number";

const digitsOf = (text: string) => text.replace(/\D/g, "");

/**
 * Whether a guest matches what was typed: their name, email or phone as
 * written, anyone named in their party, their phone with the spaces left
 * out ("0412345678" finds "0412 345 678"), or their reference number
 * with or without the dashes.
 */
function matchesSearch(r: Registration, typed: string): boolean {
  const needle = typed.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const words = [r.name, r.email, r.phone ?? "", ...r.party.map((m) => m.name)]
    .join(" ")
    .toLowerCase();
  if (words.includes(needle)) {
    return true;
  }
  const compact = needle.replace(/[\s-]/g, "");
  if (
    compact.length >= 3 &&
    r.confirmationRef.toLowerCase().replace(/-/g, "").includes(compact)
  ) {
    return true;
  }
  const digits = digitsOf(needle);
  return digits.length >= 3 && digitsOf(r.phone ?? "").includes(digits);
}

/**
 * The door itself, below the event's name: how many people are here,
 * the Scan button, a search and the walk-in form, then the guest list in
 * two headed groups, "Still to come" and "Already here", so the queue is
 * clear and nobody's row jumps without a reason. Bookings marked not
 * coming (seva) wait in a closed "Not coming" list at the bottom, where
 * they can still be checked in if they turn up. One tap checks a
 * booking in; an arrived booking opens its guest card instead of
 * undoing, so a stray tap can't un-check a family.
 */
export function ReceptionView({
  group,
  onOpenGuest,
  onScan,
  walkInOpen,
  onWalkInOpenChange,
  searchInputRef,
}: {
  group: EventGroup;
  /** Tap a name to open the guest card (Reception page supplies it). */
  onOpenGuest?: (r: Registration) => void;
  /** Open the camera. */
  onScan?: () => void;
  /** The walk-in form is open. Reception holds it so the guest card's
   *  "Add as a walk-in" can open it. */
  walkInOpen: boolean;
  onWalkInOpenChange: (open: boolean) => void;
  /** The search box, so the guest card's "Search by name" can focus it. */
  searchInputRef: RefObject<HTMLInputElement | null>;
}) {
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | null>(null);

  const utils = trpc.useUtils();
  const checkIn = trpc.adminCheckInRegistration.useMutation();

  useEffect(
    () => () => {
      if (noticeTimer.current !== null) {
        window.clearTimeout(noticeTimer.current);
      }
    },
    [],
  );

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

  // A check-in that fails on venue wifi used to leave the button
  // looking exactly as if it had never been pressed, so a volunteer
  // could work the whole door and record none of it. Name the person
  // and keep the failure on screen until they retry it.
  const [failed, setFailed] = useState<Map<string, string>>(new Map());

  const toggle = (id: string, checkedIn: boolean, who?: string) => {
    setBusy(id, true);
    setFailed((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    checkIn.mutate(
      { id, checkedIn },
      {
        onError: () => {
          setFailed((prev) => new Map(prev).set(id, who ?? "This person"));
        },
        onSettled: () => {
          setBusy(id, false);
          void utils.adminListEventRegistrations.invalidate();
        },
      },
    );
  };

  const walkInAdded = (name: string) => {
    onWalkInOpenChange(false);
    void utils.adminListEventRegistrations.invalidate();
    setNotice(`${name} added and checked in.`);
    if (noticeTimer.current !== null) {
      window.clearTimeout(noticeTimer.current);
    }
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4000);
  };

  const { toCome, here, notComing } = useMemo(() => {
    const byName = (a: Registration, b: Registration) =>
      a.name.localeCompare(b.name);
    const matched = group.registrations.filter((r) => matchesSearch(r, search));
    return {
      toCome: matched
        .filter((r) => !r.checkedInAt && !isNotComing(r))
        .sort(byName),
      here: matched.filter((r) => r.checkedInAt).sort(byName),
      notComing: matched.filter((r) => isNotComing(r)).sort(byName),
    };
  }, [group.registrations, search]);

  const walkIns = group.registrations.filter((r) => r.isWalkIn).length;
  // People, not bookings: a family of four with two through the door is
  // two still to come.
  const stillToCome = Math.max(0, group.totalPeople - group.checkedInPeople);
  const searching = search.trim().length > 0;

  const renderRow = (r: Registration) => (
    <GuestRow
      key={r.id}
      r={r}
      busy={busyIds.has(r.id)}
      failedName={failed.get(r.id) ?? null}
      editing={editingId === r.id}
      onOpen={onOpenGuest ? () => onOpenGuest(r) : null}
      onCheckIn={() => toggle(r.id, true, r.name)}
      onUndo={() => toggle(r.id, false, r.name)}
      onToggleEdit={() => setEditingId((cur) => (cur === r.id ? null : r.id))}
      onEditClose={() => setEditingId(null)}
      onEdited={() => {
        setEditingId(null);
        void utils.adminListEventRegistrations.invalidate();
      }}
    />
  );

  return (
    <div className="mt-4">
      <div>
        <p className="text-[28px] leading-tight font-bold text-[color:var(--color-ink)] tabular-nums">
          {group.checkedInPeople} of {group.totalPeople}{" "}
          {group.totalPeople === 1 ? "person" : "people"} here
        </p>
        <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)] tabular-nums">
          {stillToCome} still to come
          {walkIns > 0
            ? ` · ${walkIns} ${walkIns === 1 ? "walk-in" : "walk-ins"}`
            : ""}
        </p>
      </div>

      {onScan ? (
        <div className="mt-5">
          <button
            type="button"
            onClick={onScan}
            className="inline-flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[color:var(--color-amber)] px-8 text-[18px] font-semibold text-[color:var(--color-bg)] transition-opacity hover:opacity-90 md:w-auto md:min-w-80"
          >
            <ScanLine size={24} aria-hidden="true" /> Scan a pass
          </button>
          <p className="mt-2 text-[15px] text-[color:var(--color-ink-soft)]">
            Point the camera at the QR code on their phone or printed pass. The
            reference number under it works too.
          </p>
        </div>
      ) : null}

      <KundGround
        registrations={group.registrations}
        onOpenGuest={onOpenGuest}
      />

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className={`${FIELD_LABEL} flex-1`}>
          {SEARCH_LABEL}
          <span className="flex min-h-12 items-center gap-2.5 rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3.5 transition-colors focus-within:border-[color:var(--color-amber)]">
            <Search
              size={18}
              aria-hidden="true"
              className="shrink-0 text-[color:var(--color-ink-soft)]"
            />
            <input
              ref={searchInputRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              enterKeyHint="search"
              className="w-full min-w-0 bg-transparent py-2 text-[16px] font-normal text-[color:var(--color-ink)] outline-none"
            />
          </span>
        </label>
        <button
          type="button"
          onClick={() => onWalkInOpenChange(!walkInOpen)}
          aria-expanded={walkInOpen}
          className={`${SECONDARY_BUTTON} font-semibold`}
        >
          <UserPlus size={18} aria-hidden="true" />
          Add a walk-in
        </button>
      </div>

      {walkInOpen ? (
        <WalkInForm
          eventSlug={group.eventSlug}
          onAdded={walkInAdded}
          onCancel={() => onWalkInOpenChange(false)}
        />
      ) : null}

      <div role="status" aria-live="polite">
        {notice ? (
          <p className="mt-4 flex items-center gap-2 rounded-lg border border-[color:var(--color-success)]/40 px-4 py-3 text-[16px] font-medium text-[color:var(--color-success)]">
            <Check size={18} aria-hidden="true" /> {notice}
          </p>
        ) : null}
      </div>

      {group.registrations.length === 0 ? (
        <p className="empty-state">
          No bookings for this event yet. Use Add a walk-in for anyone who
          arrives.
        </p>
      ) : toCome.length + here.length + notComing.length === 0 ? (
        <p className="empty-state">
          {`No one matches "${search.trim()}". Try part of their name or their phone number.`}
        </p>
      ) : (
        <>
          {toCome.length > 0 || !searching ? (
            <section className="mt-7">
              <h2 className="text-[15px] font-semibold text-[color:var(--color-ink)]">
                Still to come ({toCome.length})
              </h2>
              {toCome.length > 0 ? (
                <ul className="mt-2 space-y-2">{toCome.map(renderRow)}</ul>
              ) : (
                <p className="mt-2 text-[16px] text-[color:var(--color-ink-soft)]">
                  {notComing.length === 0
                    ? "Everyone on the list is here."
                    : here.length > 0
                      ? "Everyone who is coming is here."
                      : "No one on the list is coming."}
                </p>
              )}
            </section>
          ) : null}
          {here.length > 0 ? (
            <section className="mt-7">
              <h2 className="text-[15px] font-semibold text-[color:var(--color-ink)]">
                Already here ({here.length})
              </h2>
              <ul className="mt-2 space-y-2">{here.map(renderRow)}</ul>
            </section>
          ) : null}
          {notComing.length > 0 ? (
            // Closed until wanted; a search that finds someone here opens it.
            <details className="group mt-7" open={searching || undefined}>
              <summary className="flex min-h-11 list-none items-center gap-2 text-[15px] font-semibold text-[color:var(--color-ink)] [&::-webkit-details-marker]:hidden">
                Not coming ({notComing.length})
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className="shrink-0 text-[color:var(--color-ink-soft)] transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
                Marked as not coming. If they turn up, check them in as usual.
              </p>
              <ul className="mt-2 space-y-2">{notComing.map(renderRow)}</ul>
            </details>
          ) : null}
        </>
      )}
    </div>
  );
}

/** One booking on the door list. */
function GuestRow({
  r,
  busy,
  failedName,
  editing,
  onOpen,
  onCheckIn,
  onUndo,
  onToggleEdit,
  onEditClose,
  onEdited,
}: {
  r: Registration;
  busy: boolean;
  /** Set when this booking's last check-in didn't save. */
  failedName: string | null;
  editing: boolean;
  /** Opens the guest card; null where there is no card to open. */
  onOpen: (() => void) | null;
  onCheckIn: () => void;
  onUndo: () => void;
  onToggleEdit: () => void;
  onEditClose: () => void;
  onEdited: () => void;
}) {
  const present = Boolean(r.checkedInAt);
  const hereCount = r.party.filter((m) => m.arrivedAt).length;
  return (
    <li
      className={`rounded-xl border transition-colors ${
        present
          ? "border-[color:var(--color-amber)]/40 bg-[color:var(--color-amber)]/8"
          : "border-[color:var(--color-ink)]/20 bg-[color:var(--color-paper)]"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <div className="min-w-0 basis-full sm:flex-1 sm:basis-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {onOpen ? (
              <button
                type="button"
                onClick={onOpen}
                aria-label={`Details for ${r.name}`}
                className="inline-flex min-h-11 min-w-0 items-center gap-1 text-left text-[17px] leading-snug font-semibold text-[color:var(--color-ink)] underline-offset-4 hover:underline"
              >
                <span className="min-w-0 break-words">{r.name}</span>
                <ChevronRight
                  size={18}
                  aria-hidden="true"
                  className="shrink-0 text-[color:var(--color-ink-soft)]"
                />
              </button>
            ) : (
              <span className="text-[17px] leading-snug font-semibold break-words text-[color:var(--color-ink)]">
                {r.name}
              </span>
            )}
            {r.isWalkIn ? (
              <span className="badge border border-[color:var(--color-ink)]/30 text-[color:var(--color-ink)]">
                Walk-in
              </span>
            ) : null}
            {/* Meal coupons and the Kund email need one: Edit adds it. */}
            {r.email ? null : (
              <span className="badge border border-red-700/50 bg-red-700/[0.07] font-semibold text-red-700">
                No email
              </span>
            )}
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-[color:var(--color-ink-soft)]">
            <span className="tabular-nums">{peopleSummary(r)}</span>
            {!present && hereCount > 0 ? (
              <span className="font-semibold text-[color:var(--color-amber)] tabular-nums">
                {hereCount} of {r.party.length} here
              </span>
            ) : null}
            {r.phone ? (
              <a
                href={`tel:${r.phone.replace(/\s+/g, "")}`}
                className="underline underline-offset-4 hover:text-[color:var(--color-ink)]"
              >
                {r.phone}
              </a>
            ) : null}
            {r.email ? (
              <span className="min-w-0 break-all">{r.email}</span>
            ) : null}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleEdit}
            aria-label={`Edit ${r.name}`}
            aria-expanded={editing}
            className={`inline-flex min-h-12 items-center justify-center gap-1.5 rounded-full border px-4 text-[14px] font-medium transition-colors ${
              editing
                ? "border-[color:var(--color-ink)] bg-[color:var(--color-ink)] text-[color:var(--color-paper)]"
                : "border-[color:var(--color-line-strong)] text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)]"
            }`}
          >
            <Pencil size={15} aria-hidden="true" />
            Edit
          </button>
          {present ? (
            <button
              type="button"
              onClick={onOpen ?? onUndo}
              className="inline-flex min-h-12 items-center justify-center gap-1.5 rounded-full bg-[color:var(--color-amber)]/15 px-4 text-[15px] font-semibold text-[color:var(--color-amber)] tabular-nums"
            >
              <Check size={16} aria-hidden="true" />
              Here{r.checkedInAt ? ` ${clockTime(r.checkedInAt)}` : ""}
            </button>
          ) : (
            <button
              type="button"
              onClick={onCheckIn}
              disabled={busy}
              className="inline-flex min-h-12 min-w-[7.5rem] items-center justify-center rounded-full border-2 border-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-amber)] transition-colors hover:bg-[color:var(--color-amber)] hover:text-[color:var(--color-bg)] disabled:opacity-50"
            >
              {busy
                ? "Saving…"
                : r.totalPeople > 1
                  ? `Check in all ${r.totalPeople}`
                  : "Check in"}
            </button>
          )}
        </div>
        {failedName ? (
          <p
            role="alert"
            className="order-last basis-full rounded-lg border border-red-700/40 bg-red-700/[0.07] px-3 py-2 text-[15px] font-medium text-red-700"
          >
            {failedName}&apos;s check-in didn&apos;t save. Check your signal and
            tap Check in again.
          </p>
        ) : null}
      </div>

      {editing ? (
        <RegistrationEditor
          registration={r}
          onClose={onEditClose}
          onSaved={onEdited}
        />
      ) : null}
    </li>
  );
}

/**
 * The walk-in form at the door. A name and how many people are all it
 * needs; phone is optional. They're recorded as arrived the moment
 * they're added.
 */
function WalkInForm({
  eventSlug,
  onAdded,
  onCancel,
}: {
  eventSlug: string;
  onAdded: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const addWalkIn = trpc.adminAddWalkIn.useMutation();

  const submit = () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please enter their name.");
      return;
    }
    addWalkIn.mutate(
      {
        eventSlug,
        name: trimmed,
        phone: phone.trim() || undefined,
        adults,
        children,
      },
      {
        onSuccess: () => onAdded(trimmed),
        onError: (err) => setError(friendlyError(err)),
      },
    );
  };

  return (
    <div className="mt-4 rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-paper)] p-5">
      <h2 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
        Add a walk-in
      </h2>
      <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
        For someone who hasn&apos;t booked. They&apos;re marked as arrived
        straight away.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className={FIELD_LABEL}>
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            autoComplete="off"
            autoFocus
            className={FIELD}
          />
        </label>
        <label className={FIELD_LABEL}>
          Phone (optional)
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            autoComplete="off"
            className={FIELD}
          />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
        <NumberStepper label="Adults" value={adults} onChange={setAdults} />
        <NumberStepper
          label="Children"
          value={children}
          onChange={setChildren}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-3 text-[15px] font-medium text-red-700">
          {error}
        </p>
      ) : null}
      <div className="mt-5 flex flex-wrap gap-2.5">
        <button
          type="button"
          onClick={submit}
          disabled={addWalkIn.isPending}
          className={`${PRIMARY_BUTTON} bg-[color:var(--color-amber)] text-[color:var(--color-bg)]`}
        >
          <Check size={18} aria-hidden="true" />
          {addWalkIn.isPending ? "Adding…" : "Add and check in"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={addWalkIn.isPending}
          className={SECONDARY_BUTTON}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
